import { faEdit, faGear, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Dropdown, Flex, Table } from 'components';
import { Button, Select, TextField } from 'components/_form';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { AreaPathChips } from 'components/AreaPathChips/AreaPathChips';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IUserFunction } from 'types/functions';
import { displayTranslatedField } from 'utilities/localizedString';

const Icon = styled(Flex)<{ isActive?: boolean }>`
  justify-content: center;
  ::after {
    content: '';
    width: 15px;
    height: 15px;
    border-radius: 15px;
    background-color: ${({ isActive }) => (isActive ? 'green' : 'grey')};
  }
`;

interface IRolesAndFunctionsTable {
  highlightCells: boolean;
  functions: IUserFunction[];
  onEdit: (fn: IUserFunction) => void;
}

const RolesAndFunctionsTable = ({
  highlightCells,
  functions,
  onEdit
}: IRolesAndFunctionsTable) => {
  const { t } = useTranslation();
  const { data: areasData = [] } = useAreasQuery();
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(25);
  const [search, setSearch] = useState<string>('');

  const areaLabelById = useMemo(
    () =>
      new Map(
        areasData.map((area) => [
          String(area.id),
          area.materialized_path_label
        ])
      ),
    [areasData]
  );

  const header: string[] = [
    t('employeesAndUsersView.tableHeaders.active'),
    t('employeesAndUsersView.tableHeaders.name'),
    t('employeesAndUsersView.tableHeaders.area'),
    ''
  ];

  const data = useMemo(() => {
    const q = search.toLowerCase();
    const list = functions.filter((fn) => {
      if (!search) return true;
      const displayName = displayTranslatedField(fn, 'name').toLowerCase();
      const inTranslations = Object.values(fn.name_translations ?? {}).some((v) =>
        v.toLowerCase().includes(q)
      );
      const areaLabels = (fn.area_ids ?? []).map((id) => areaLabelById.get(String(id)));
      return (
        displayName.includes(q) ||
        inTranslations ||
        fn.name.toLowerCase().includes(q) ||
        areaLabels.join(' ').toLowerCase().includes(q)
      );
    });
    return list.map((fn) => {
      const areaChips = (fn.area_ids ?? [])
        .map((id) => ({
          id: String(id),
          label: areaLabelById.get(String(id))
        }))
        .filter((x): x is { id: string; label: string } => Boolean(x.label));

      return {
        active: <Icon isActive={fn.state === 'active'} key={`active-${fn.id}`} />,
        name: displayTranslatedField(fn, 'name'),
        obszar: (
          <AreaPathChips
            chips={areaChips.map(({ id, label }) => ({
              key: `${fn.id}-area-${id}`,
              label
            }))}
          />
        ),
        edit: (
          <Box mx="auto" width="fit-content" key={`edit-${fn.id}`}>
            <Button
              variant="eucalyptus"
              onClick={() => onEdit(fn)}
              icon={<FontAwesomeIcon icon={faEdit} />}
            />
          </Box>
        )
      };
    });
  }, [functions, search, onEdit, areaLabelById]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * perPage;
    return data.slice(start, start + perPage);
  }, [data, page, perPage]);

  const additionalHeader = (
    <>
      <FontAwesomeIcon icon={faSearch} />
      <TextField
        hideError
        value={search}
        onChange={(e) => setSearch(e.target.value ?? '')}
        placeholder={t('employeesAndUsersView.form.search') ?? undefined}
      />
      <Dropdown items={[]}>
        <FontAwesomeIcon icon={faGear} />
      </Dropdown>
      <Select
        placeholder={t('employeesAndUsersView.form.chooseArea') ?? undefined}
        options={[]}
        withoutMargin
      />
    </>
  );

  return (
    <Table
      header={header}
      data={paginatedData}
      pagination={{
        page,
        setPage,
        perPage,
        total: data.length,
        setPerPage
      }}
      highlightCells={highlightCells}
      leftFixedKeys={[0, 1]}
      rightFixedKeys={[header.length - 1]}
      additionalHeaderElement={additionalHeader}
    />
  );
};

export default RolesAndFunctionsTable;
